'use client';

import * as React from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';

import { useHotelMapModal } from '@/app/components/hotel-detail/HotelMapModalProvider';

import { trackViewMap } from '@/app/components/hotel-detail/trackInteractionEvents';

import { AmenitiesList, HeroFacilitiesSummary } from '@/app/components/hotel-detail/HeroFacilitiesSummary';
import { HeroHotelLocation } from '@/app/components/hotel-detail/HeroHotelLocation';

import customCss from '@/app/components/hotel-detail/css/customCss';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { HotelCheckinCheckout } from '@/app/components/hotel-detail/HotelCheckinCheckout';
import { useFetchHotelStatic, useIsHotelStaticLoading } from './hooks/useFetchHotelStatic';
import { useFetchHotelPromotions } from './hooks/useFetchHotelPromotions';
import { useExtendedSortedHotelServices } from './hooks/useExtendedSortedHotelServices';

const BannerLondonEye = dynamic(() => import(/* webpackChunkName: "BannerLondonEye" */ './BannerLondonEye'));
const BannerTourDeFrance = dynamic(() => import(/* webpackChunkName: "BannerTourDeFrance" */ './BannerTourDeFrance'));

const Grid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: repeat(auto, 4);
  gap: 16px;

  @media ${customCss.breakpoints.sm} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto 1fr;

    /* A hotel with no facilities and no check-in/check-out times leaves the location banner alone
       in here (MPH-1007); without this it would auto-place into the first column and paint at half
       width against an empty half. */
    & > *:only-child {
      grid-column: 1 / -1;
    }
  }
`;

const HeroFacilitiesSummaryStyled = styled(HeroFacilitiesSummary)`
  @media ${customCss.breakpoints.sm} {
    grid-row: 1 / 3;
    grid-column: 1 / 2;

    ${AmenitiesList} {
      display: grid;
      gap: 4px 8px;
      grid-template-columns: 1fr 1fr;
      height: auto;
      width: 100%;
      margin: 0;
      padding: 0;
    }
  }
`;

export default function HeroAdditionalInfo({ openFacilitiesModal }: { openFacilitiesModal?: boolean } = {}) {
  const { data: staticData } = useFetchHotelStatic();
  const isLoading = useIsHotelStaticLoading();
  const { data: promotions } = useFetchHotelPromotions();
  const { showMap } = useHotelMapModal();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { promotionType } = promotions || {};

  const openMap = () => {
    showMap();
    trackViewMap();
    sendCuriosityClickEvent({
      event_name: 'click_view_map_from_hotel_info',
      event_context: 'from_hotel_info',
    });
  };

  const selectedServices = useExtendedSortedHotelServices(staticData?.services);
  const shouldDisplayAmenities =
    (staticData?.amenities && staticData.amenities.length > 0) || (selectedServices && selectedServices.length > 0);

  return (
    <>
      {promotionType === 'LONDON_EYE' && <BannerLondonEye />}
      {promotionType === 'TOUR_FRANCE' && <BannerTourDeFrance />}
      {/* <GuestSummarizedReview />  // TODO When enable this again enable also the prefetch in HotelAndRoomsLayout */}
      <Grid>
        {shouldDisplayAmenities && <HeroFacilitiesSummaryStyled openFacilitiesModal={openFacilitiesModal} />}
        <HotelCheckinCheckout
          checkinInformation={staticData?.checkinInformation}
          checkoutInformation={staticData?.checkoutInformation}
          isLoading={isLoading}
        />
        {staticData?.city ? (
          <HeroHotelLocation
            openMap={openMap}
            city={staticData?.city}
            distanceToCityCenter={staticData?.distanceToCityCenter}
          />
        ) : undefined}
      </Grid>
    </>
  );
}
